/**
 * Turkish message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: informal sen (locked in l10n/locales.md) —
 * imperatives in sen form (gönder, aç, dene), possessives -in/-ın
 * (cihazın, telefonun).
 *
 * Platform terms follow Turkish UI conventions (Tema for theme, sohbet
 * for conversation, eşleştir for pair, temizle for dismiss, pano for
 * clipboard); "Glance" is the untranslated feature brand name
 * (glossary D1) and "klip"/"klipler" is a locked loanword (D2).
 *
 * Plural notes: Turkish is a {one, other} CLDR locale, but a numeral is
 * always followed by the SINGULAR noun (5 cihaz, never 5 cihazlar), so
 * both forms carry the same singular noun; the split stays structural
 * and every count still routes through `selectPlural("tr", …)`.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Bağlandı",
    statusReconnecting: "Yeniden bağlanılıyor…",
    statusDisconnected: "Bağlantı kesildi",
    openSettings: "Ayarları aç",
    pairedFallbackDevice: "telefonun",
    publicModeToggleEnable: "Bildirim içeriğini gizle",
    publicModeToggleDisable: "Bildirim içeriğini göster",
  },

  notifications: {
    replyAriaLabel: "Yanıtla",
    replyPlaceholder: "Yanıtla…",
    replySend: "Gönder",
    sent: "Gönderildi",
    failedToSend: "Gönderilemedi",
    unread: "Okunmadı",
    dismiss: "Temizle",
    notificationImageAlt: "Bildirim resmi",
  },

  conversations: {
    replyAriaLabel: "Yanıtla",
    replyPlaceholder: "Yanıtla…",
    replySend: "Gönder",
    sent: "Gönderildi",
    failedToSend: "Gönderilemedi",
    dismiss: "Sohbeti temizle",
    messagesListLabel: "Bu sohbetteki mesajlar",
    deviceOwner: "Sen",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Okunmamış mesaj yok"
        : `${count} ${selectPlural("tr", count, {
            one: "okunmamış mesaj",
            other: "okunmamış mesaj",
          })}`,
  },

  list: {
    today: "Bugün",
    yesterday: "Dün",
    searchNotifications: "Bildirimlerde ara",
    clearSearch: "Aramayı temizle",
    clearAll: "Tümünü temizle",
    markAllRead: "Tümünü okundu işaretle",
    groupCheckboxLabel: "Grupla",
    noNotificationsTitle: "Henüz bildirim yok",
    noNotificationsDesc: "Telefonundan yansıtılan yeni mesajlar burada görünecek.",
    noMatchesTitle: "Eşleşme yok",
    notificationsList: "Bildirimler",
    privacyAppList: "Uygulamaya göre bildirimler",
    clear: "Temizle",
    // Turkish keeps the singular noun after a numeral, so both plural
    // forms carry the same "eşleşmeyi" — count still routes the selection.
    clearMatches: (count: number): string =>
      `${count} ${selectPlural("tr", count, { one: "eşleşmeyi", other: "eşleşmeyi" })} temizle`,
    noMatchesDesc: (query: string): string => `“${query}” ile eşleşen bildirim yok.`,
    appNotificationsGroup: (appName: string): string => `${appName} bildirimleri`,
    dismissAllFrom: (appName: string): string => `${appName} bildirimlerinin tümünü temizle`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("tr", count, { one: "mesaj", other: "mesaj" })
          : kind === "calls"
            ? selectPlural("tr", count, { one: "arama", other: "arama" })
            : selectPlural("tr", count, { one: "bildirim", other: "bildirim" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "temizle",
    kbdNavigate: "gezin",
    kbdOrSeparator: "veya",
  },

  settings: {
    back: "Bildirimlere dön",
    settings: "Ayarlar",
    appearance: "Görünüm",
    theme: "Tema",
    themeAuto: "Otomatik",
    themeLight: "Açık",
    themeDark: "Koyu",
    language: "Dil",
    languageSystemDefault: "Sistem varsayılanı",
    notificationsSection: "Bildirimler",
    osNotifications: "Sistem bildirimleri",
    callNotifications: "Arama bildirimleri",
    privacySection: "Gizlilik",
    analyticsToggle: "Anonim kullanım istatistikleri",
    analyticsToggleDescription:
      "Yalnızca sayılar ve kilometre taşları — bildirimlerin asla gönderilmez.",
    connection: "Bağlantı",
    status: "Durum",
    statusConnected: "Bağlandı",
    statusReconnecting: "Yeniden bağlanılıyor…",
    statusDisconnected: "Bağlantı kesildi",
    disconnectFromPhone: "Telefonla bağlantıyı kes",
    disconnectConfirm: "Emin misin? Bu, cihazının eşleştirmesini kaldırır.",
    yesDisconnect: "Evet, bağlantıyı kes",
    cancel: "İptal",
    aboutAndSupport: "Hakkında ve Destek",
    version: "Sürüm",
    encryption: "Şifreleme",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Yardım mı lazım? __EMAIL__ adresine e-posta gönder",
    disconnectFailed: "Bağlantı kesilemedi — lütfen tekrar dene",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Bağlantı kesildi ancak yerel temizleme başarısız oldu: ${errorMsg}. Lütfen sayfayı yeniden yükle.`,
    disconnectLocalCleanupFailed:
      "Bağlantı kesildi ancak yerel temizleme başarısız oldu. Lütfen sayfayı yeniden yükle.",
  },

  pairing: {
    enterCodeInstruction: "Bu kodu Reflecto uygulamasına gir",
    pairedSuccessfully: "Başarıyla eşleştirildi!",
    pairingFailed: "Eşleştirme başarısız",
    requestingPairingCode: "Eşleştirme kodu isteniyor…",
    codeExpired: "Kodun süresi doldu",
    tryAgain: "Tekrar Dene",
    failedToStartPairing: "Eşleştirme başlatılamadı",
    needAppText: "Android uygulaması mı lazım? Erişim için __EMAIL__ adresine e-posta gönder.",
  },

  dnd: {
    title: "Rahatsız etmeyin",
    description: "Bu cihazdaki bildirimleri bir saatliğine duraklat.",
    snooze1h: "1 saat duraklat",
    snoozeActiveUntil: (formattedTime: string): string => `${formattedTime} saatine kadar etkin`,
    cancelSnooze: "İptal",
  },

  connectors: {
    title: "Bağlı hizmetler",
    emptyBody:
      "Henüz bağlı hizmet yok. ChatGPT veya Claude'u kendi bağlayıcı ayarlarından bağla, ardından telefonunda kodu onayla.",
    scopeSend: "Bildirim gönderebilir",
    added: (date: string): string => `${date} tarihinde eklendi`,
    lastUsed: (relative: string): string => `En son ${relative} kullanıldı`,
    neverUsed: "Hiç kullanılmadı",
    revoke: "Bağlantıyı kes",
    revokeConfirm: (label: string): string =>
      `${label} bağlantısı kesilsin mi? Erişimi hemen kaybeder.`,
    revokeAction: "Bağlantıyı kes",
    revokeConfirmLabel: (label: string): string => `${label} bağlantısını kes`,
    cancel: "İptal",
    loadError: "Bağlı hizmetler yüklenemedi. Lütfen tekrar dene.",
    revokeError: "Hizmetin bağlantısı kesilemedi. Lütfen tekrar dene.",
    retry: "Yeniden dene",
  },

  feedback: {
    title: "Geri bildirim gönder",
    close: "Kapat",
    messageLabel: "Mesajın",
    messagePlaceholder:
      "Bir hata mı buldun, bir fikrin mi var ya da bir şey yanlış mı hissettiriyor? Bize anlat.",
    categoryLabel: "Kategori",
    categoryBug: "Hata",
    categoryIdea: "Fikir",
    categoryOther: "Diğer",
    emailLabel: "E-posta (isteğe bağlı, yalnızca yanıt istiyorsan)",
    emailPlaceholder: "sen@ornek.com",
    emailHelp: "Seni hiçbir listeye eklemeyiz.",
    disclosure:
      "Mesajın Reflecto'nun sunucusuna gönderilir ve dahili sorun izleyicimize kaydedilir. Raporları ilişkilendirebilmek için uygulama sürümünü ve eşleştirme sırasında atanan bir cihaz kimliğini ekleriz. Bildirimlerini, kişilerini veya herhangi bir kişisel veriyi görmeyiz. E-posta isteğe bağlıdır — yalnızca yanıt istiyorsan ver. Seni hiçbir listeye eklemeyiz.",
    submit: "Geri bildirim gönder",
    sending: "Gönderiliyor…",
    success: "Teşekkürler — bir insan tarafından, genellikle 24 saat içinde okunur",
    error: "Gönderilemedi. Lütfen tekrar dene.",
    done: "Bitti",
    sendFeedback: "Geri bildirim gönder",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Kapat",
    aiSummaryTag: "Yapay zeka özeti",
    errorAi: "Şu anda özet oluşturulamadı.",
    retryCta: "Tekrar dene",
    downloadHeading: "Yapay zeka özetleri al",
    downloadBody:
      "Cihazında çalışan bir yapay zeka özetiyle kaçırdıklarını yakala. Tamamen cihazında çalışır.",
    downloadCta: "Modeli indir",
    downloadPrivacyNote: "Tek seferlik bir indirme. Sunucu bildirimlerini asla görmez.",
    downloadingHeading: "Model indiriliyor…",
    caughtUp: "Her şeyi gördün — şu anda ilgilenmen gereken bir şey yok.",
    needsYou: "İlgilenmen gerekenler",
    quiet: "Diğer",
    scanning: (pending: number): string => `${pending} öğe özetleniyor…`,
    jump: "Bildirimi aç",
    affordanceReply: "Yanıtla",
    affordanceOpen: "Aç",
    affordanceCopyOtp: "Kodu kopyala",
    buttonLabel: "Glance",
  },

  update: {
    title: "Güncelleme mevcut",
    description: "Reflecto'nun yeni bir sürümü hazır. Güncellemek için yeniden yükle.",
    reload: "Yeniden yükle",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Bu cihaza “${name}” adını verdik.`,
    cardLooksGood: "İyi görünüyor",
    cardEdit: "Düzenle",
    cardAriaLabel: "Cihaz adını onayla",
    renameTitle: "Bu cihazı yeniden adlandır",
    renameLabel: "Cihaz adı",
    renameSave: "Kaydet",
    renameCancel: "İptal",
    renameFailed: "Yeniden adlandırılamadı. Lütfen tekrar dene.",
  },

  collision: {
    title: (name: string): string => `Zaten bir “${name}” var`,
    subtitle: "Buna ne diyelim?",
    chipWork: (name: string): string => `İş ${name}`,
    chipHome: (name: string): string => `Ev ${name}`,
    chipPersonal: (name: string): string => `Kişisel ${name}`,
    customLabel: "Özel ad",
    save: "Kaydet",
    useAnyway: "Yine de özgün adı kullan",
    saveFailed: "Yeni ad kaydedilemedi. Lütfen tekrar dene.",
    ariaLabel: "Yinelenen cihaz adını çöz",
  },

  clipboard: {
    title: "Pano",
    empty: "Diğer cihazlarından gelen klipler burada görünür. Kopyalamak için dokun.",
    from: (label: string): string => `kaynak: ${label}`,
    copy: "Panoya kopyala",
    copied: "Kopyalandı",
    reveal: "Göster",
    hide: "Gizle",
    maskedHint: "İçerik gizli — göstermek için Göster'e bas",
    sensitiveChip: "Hassas",
    cloudChip: "API jetonundan",
    sendPlaceholder: "Göndermek için metin yapıştır…",
    sendCta: "Gönder",
    sendSensitiveToggle: "Hassas olarak işaretle",
    sendSuccess: (n: number): string =>
      `${n} ${selectPlural("tr", n, { one: "cihaza", other: "cihaza" })} gönderildi.`,
    sendPartial: (n: number, total: number): string =>
      `${total} ${selectPlural("tr", total, { one: "cihazdan", other: "cihazdan" })} ${n} tanesine ulaşıldı.`,
    sendNoDevices: "Gönderilecek eşleştirilmiş cihaz yok.",
    sendFailed: "Gönderilemedi. Lütfen tekrar dene.",
    sendEmpty: "Gönderilecek bir şey yok.",
    dockExpand: "Pano geçmişini göster",
    dockCollapse: "Daralt",
    dockSendTitle: "Cihazlarına gönder",
    dockFromClipboard: (chars: number): string =>
      `Panodan · ${chars} ${selectPlural("tr", chars, { one: "karakter", other: "karakter" })}`,
    dockSend: (n: number): string => `${n} cihaza gönder`,
    dockCancel: "İptal",
    dockRecentHeading: "Son",
    dockEmptyClipboard: "Panon boş.",
    shareTitle: "Cihazlarına gönder",
    shareReview:
      "Ne gönderdiğini gözden geçir. Eşleştirilmiş cihazlarına uçtan uca şifreli olarak gider.",
    shareSend: "Gönder",
    shareCancel: "İptal",
    shareNotPaired: "Paylaşılan metni göndermek için önce bir cihaz eşleştir.",
    shareOpenApp: "Reflecto'yu aç",
  },
  install: {
    title: "Reflecto'yu yükle",
    body: "Daha hızlı erişim için ana ekranına ekle.",
    cta: "Yükle",
    dismissAria: "Yükleme istemini kapat",
  },
  common: {
    loading: "Yükleniyor",
  },
  errors: {
    notPaired: "Eşleştirilmemiş",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} (${os})`,
    webFallback: "Web Tarayıcısı",
  },

  palette: {
    placeholder: "Bir komut yaz…",
    noResults: "Eşleşen komut yok",
    ariaLabel: "Komut paleti",
    clearAll: "Tüm bildirimleri temizle",
    glance: "Glance özeti",
    cancelSnooze: "Duraklatmayı iptal et",
    copyClip: "Alınan son klibi kopyala",
  },
} satisfies MessagesShape;
